import {
  FREQUENCY_OPTIONS,
  RedemptionBy,
  REMDEMPTION_BY_OPTIONS,
  TransactionOrderType,
} from "creso-core/constants";
import {
  useCreateStpTransaction,
  useUpdateStpTransaction,
  useHoldingsData,
  useScheme,
  useGetHoldingFolios,
} from "creso-core/hooks";
import { isSTPOrder } from "creso-core/utils";
import {
  FormLabel,
  Form,
  FormItem,
  FormField,
  V2Loader,
  FormInput,
  FormSelect,
  FormCheckbox,
  FormMessage,
  cn,
  FormDescription,
  FormCurrencyInput,
} from "web-design-system";
import { useCallback, useContext, useMemo } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { stpFormValidationSchema } from "creso-core/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, parse } from "date-fns";
import { handleFormValidationErrors } from "@/utils/error";
import {
  HoldingDetails,
  HoldingFoliosDropdown,
  HoldingSchemesDropdown,
  PlanStartDate,
} from "web-core";
import { TransactionContext } from "./transaction-wizard";
import { SchemesSearchDropdown } from "./schemes-search-dropdown";
import FormActions from "./form-actions";

type FormData = z.infer<typeof stpFormValidationSchema>;

function getDefaultFormData(): FormData {
  return {
    switch_out_scheme: null,
    folio: null,
    switch_in_scheme: null,
    number_of_transfers: "",
    frequency: "MONTHLY",
    transferBy: RedemptionBy.AMOUNT,
    amount: null,
    units: null,
    start_date: "",
    first_order_today: true,
  };
}

export default function STPForm() {
  const {
    selectedOrder,
    investment_account_guid = "",
    updateContext,
  } = useContext(TransactionContext);

  const switchInSchemeId =
    selectedOrder?.type === TransactionOrderType.STP
      ? selectedOrder?.switch_in_scheme_id
      : null;

  const { data: switchInScheme, isLoading: isSwitchInSchemeLoading } =
    useScheme(switchInSchemeId || 0, "stp");

  const { data: holdingSchemes, isLoading: isHoldingSchemesLoading } =
    useHoldingsData(investment_account_guid);

  const scheme_isin =
    selectedOrder?.type === TransactionOrderType.STP
      ? selectedOrder.switch_out_scheme_isin
      : "";

  const { data: holdingFolios, isLoading: isHoldingFoliosLoading } =
    useGetHoldingFolios(
      investment_account_guid,
      scheme_isin || "",
      !!scheme_isin && !!investment_account_guid
    );

  const createStpTransaction = useCreateStpTransaction(investment_account_guid);

  const updateStpTranaction = useUpdateStpTransaction(investment_account_guid);

  const isCancelOrder = selectedOrder?.action_type === "CANCEL";

  const form = useForm({
    resolver: zodResolver(stpFormValidationSchema),
    values: isSTPOrder(selectedOrder)
      ? {
          switch_out_scheme:
            holdingSchemes?.find(
              (d) => d.scheme_isin === selectedOrder.switch_out_scheme_isin
            ) ?? null,
          folio:
            holdingFolios?.find((d) => d.folio_id === selectedOrder.folio_id) ??
            null,
          switch_in_scheme: switchInScheme ?? null,
          number_of_transfers: selectedOrder.number_of_transfers,
          frequency: selectedOrder.frequency || "MONTHLY",
          transferBy: selectedOrder.units
            ? RedemptionBy.UNITS
            : RedemptionBy.AMOUNT,
          amount: selectedOrder.amount ?? null,
          units: selectedOrder.units ?? null,
          start_date: selectedOrder.start_date,
          first_order_today: selectedOrder.first_order_today ?? true,
        }
      : getDefaultFormData(),
    disabled: isCancelOrder,
  });

  const onSubmit = useCallback(
    async (values: FormData) => {
      try {
        if (
          values.switch_out_scheme?.scheme_isin &&
          values.switch_in_scheme?.scheme_isin &&
          values.folio
        ) {
          const payload = {
            investment_account_guid,
            switch_out_scheme_isin: values.switch_out_scheme?.scheme_isin,
            folio_id: values.folio.folio_id,
            switch_in_scheme_isin: values.switch_in_scheme?.scheme_isin,
            number_of_transfers: values.number_of_transfers?.toString() || "",
            frequency: values.frequency,
            start_date: values.start_date,
            first_order_today: values.first_order_today,
            ...(values.transferBy === RedemptionBy.UNITS
              ? {
                  units: values.units,
                }
              : {
                  amount: values.amount,
                }),
          };
          if (selectedOrder?.id) {
            await updateStpTranaction.mutateAsync({
              id: selectedOrder.id,
              ...payload,
            });
          } else {
            await createStpTransaction.mutateAsync(payload);
          }
          form.reset(getDefaultFormData());
          updateContext?.({
            selectedOrder: null,
          });
        }
      } catch (error) {
        handleFormValidationErrors(form, error);
        toast.error(`Failed to save order details`, {
          description: (error as Error).message,
        });
      }
    },
    [
      investment_account_guid,
      createStpTransaction,
      updateStpTranaction,
      selectedOrder,
      updateContext,
      form,
    ]
  );

  const startDate = form.watch("start_date");

  const formSwitchInScheme = form.watch("switch_in_scheme");

  const days = useMemo(() => {
    return formSwitchInScheme?.dates ?? undefined;
  }, [formSwitchInScheme]);

  const nextInstallmentDate = useMemo(() => {
    try {
      const startDateParsed = parse(startDate, "yyyy-MM-dd", new Date());
      return format(startDateParsed, "dd MMM yyyy");
    } catch {
      return "";
    }
  }, [startDate]);

  return (
    <div className="flex flex-col flex-1">
      <h5 className="mt-1">
        {selectedOrder?.id
          ? isCancelOrder
            ? "STP Cancellation"
            : "Update STP order"
          : "Create STP order"}
      </h5>
      <div className="h-px bg-border w-full mt-3 mb-4"></div>
      <V2Loader
        className="relative"
        isLoading={
          isSwitchInSchemeLoading ||
          isHoldingSchemesLoading ||
          isHoldingFoliosLoading
        }
      >
        {isCancelOrder && (
          <div className="z-50 size-full bg-background/50 absolute top-0 left-0" />
        )}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 flex-1 px-1 flex flex-col items-start"
          >
            <div className="grid grid-cols-5 w-full gap-2 border-b pb-4 border-dashed">
              <FormField
                name="switch_out_scheme"
                className="col-span-2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From scheme</FormLabel>
                    <HoldingSchemesDropdown
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        form.setValue("folio", null);
                        const switchInScheme =
                          form.getValues("switch_in_scheme");
                        if (switchInScheme?.amc_id !== value?.amc_id) {
                          form.setValue("switch_in_scheme", null);
                        }
                      }}
                      investmentAccountGuid={investment_account_guid}
                      schemefilter={(scheme) => scheme.stp_allowed}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="folio"
                className="col-span-1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Folio</FormLabel>
                    <HoldingFoliosDropdown
                      value={field.value}
                      onChange={field.onChange}
                      investmentAccountGuid={investment_account_guid}
                      schemeIsin={form.watch("switch_out_scheme")?.scheme_isin}
                      filter={(folio) => folio.stp_allowed}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="switch_in_scheme"
                className="col-span-2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>To scheme</FormLabel>
                    <SchemesSearchDropdown
                      placeholder="Select scheme"
                      value={
                        field.value
                          ? {
                              label: field.value.scheme_name,
                              value: field.value.scheme_isin,
                            }
                          : null
                      }
                      frequency={form.watch("frequency")}
                      onChange={(value) => {
                        field.onChange(value);
                        form.setValue("start_date", "");
                      }}
                      type="stp"
                      amc_id={
                        form.watch("switch_out_scheme")?.amc_id ?? undefined
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("folio") && (
                <HoldingDetails
                  className="col-span-5"
                  amount={form.watch("folio")?.amount || ""}
                  units={form.watch("folio")?.units || ""}
                  nav={form.watch("folio")?.nav || ""}
                />
              )}
            </div>
            <h6>Installment details</h6>
            <div className="grid grid-cols-2 w-full gap-2">
              <FormField
                className="col-span-1"
                name="number_of_transfers"
                render={({ field }) => (
                  <FormInput
                    label="Installment count"
                    type="number"
                    {...field}
                  />
                )}
              />
              <FormField
                className="col-span-1"
                name="frequency"
                render={({ field }) => (
                  <FormSelect
                    options={FREQUENCY_OPTIONS}
                    {...field}
                    label="Frequency"
                    onChange={(value) => {
                      field.onChange(value);
                      form.setValue("start_date", "");
                      if (value === "DAILY") {
                        form.setValue("first_order_today", false);
                      }
                    }}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-10 w-full gap-2">
              <FormField
                className="col-span-3"
                name="transferBy"
                render={({ field }) => (
                  <FormSelect
                    label="Transfer by"
                    options={REMDEMPTION_BY_OPTIONS}
                    {...field}
                  />
                )}
              />
              <FormField
                className={cn(
                  "col-span-3",
                  form.watch("transferBy") === RedemptionBy.AMOUNT && "hidden"
                )}
                control={form.control}
                name="units"
                render={({ field }) => (
                  <FormInput
                    type="number"
                    label="Units"
                    {...field}
                    value={field.value || ""}
                  />
                )}
              />
              <FormField
                className={cn(
                  "col-span-3",
                  form.watch("transferBy") === RedemptionBy.UNITS && "hidden"
                )}
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormCurrencyInput
                    label="Amount"
                    {...field}
                    value={field.value || ""}
                  />
                )}
              />
              <FormField
                control={form.control}
                className={cn("col-span-4")}
                name="start_date"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Start date</FormLabel>
                    <PlanStartDate
                      planType="STP"
                      frequency={form.watch("frequency")}
                      firstOrderToday={form.watch("first_order_today")}
                      placeholder="Select start date"
                      onChange={field.onChange}
                      value={field.value}
                      days={days}
                    />
                    {field.value && (
                      <FormDescription>
                        {form.watch("first_order_today") ? "Next" : "First"}{" "}
                        installment on {nextInstallmentDate}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              name="first_order_today"
              render={({ field }) => (
                <FormCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  label="First order today"
                  disabled={form.watch("frequency") === "DAILY"}
                />
              )}
            />
            <FormActions
              dirty={form.formState.isDirty}
              onReset={() => form.reset()}
              isUpdate={!!selectedOrder?.id}
            />
          </form>
        </Form>
      </V2Loader>
    </div>
  );
}
